<template>
  <div
    v-if="loading"
    class="wrap skeleton-flex"
  >
    <UiSkeletonItem
      v-for="el in 4"
      :key="el"
      :style="{
        width: '100px',
        height: '32px',
        borderRadius: '6px',
      }"
      :animated="true"
    />
  </div>

  <GeoList
    v-else
    v-view-tracker="getParams"
    :list="list"
    :link-type="linkType"
    @item-click="itemClick"
  >
    <template #btn>
      <UiButton
        v-if="isCountries"
        data-test="button-city-more"
        variant="outline"
        color-mode="neutral"
        size="small"
        :text="buttonTitle"
        :full-size="isMobile"
        @click="[changeModalStatus(), soguClickBtn()]"
      />
    </template>
  </GeoList>

  <UiModal
    :is-shown="isModalShow"
    :size="modalSize"
    :teleport-target="modalTarget || undefined"
    :title="title"
    is-footer-hidden
    @close="changeModalStatus"
  >
    <template #content>
      <GeoListAll
        :link-type="linkType"
        :countries="list"
        @item-click="modalItemClick"
      />
    </template>
  </UiModal>
</template>

<script lang="ts" setup>
  import { UiModal } from '@experience/core-modal-ui';
  import { useI18n } from '@experience/i18n';
  import { UiButton, UiSkeletonItem } from '@experience/vue3-kit';
  import { ref, computed, onMounted } from 'vue';

  import { trackEvent, useViewTracker } from '@/services/analytics';
  import GeoList from '@/widgets/GeoList.vue';
  import GeoListAll from '@/widgets/GeoListAll.vue';
  import { ru } from '@/features/JournalCountries/i18n/ru';
  import { initModalTeleportTarget } from '@/shared/lib/initModalTeleportTarget';
  import { applyModalStyles, resetModalStyles } from '@/shared/lib/modalStyles';
  import type { GeoItem } from '@/shared/models/journal';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const { isMobile, isDesktop, fromDesktopMedium, isTablet } = useScreenSize();
  const isModalShow = ref(false);
  const modalTarget = ref<HTMLElement | null>(null);

  interface Props {
    list: GeoItem[];
    linkType: 'city' | 'country' | 'region';
    loading: boolean;
    slantedText?: string;
    seoPrefix?: string;
    geoSubject?: {
      id: number;
      name: string;
    };
    geoType?: string;
  }

  const props = defineProps<Props>();
  onMounted(() => {
    initModalTeleportTarget(modalTarget);
  });

  const title = computed(() => {
    const text = props.slantedText || '';

    if (props.linkType === 'city') {
      return t('articleOnCity', { text });
    }
    if (props.linkType === 'country') {
      return t('articleOnCountry', { text });
    }
    if (props.linkType === 'region') {
      return t('articleOnRegion', { text });
    }

    return '';
  });

  const changeModalStatus = () => {
    isModalShow.value = !isModalShow.value;

    if (isModalShow.value) {
      applyModalStyles();
    } else {
      resetModalStyles();
    }
  };

  const citiesIds = computed(() =>
    props.list.map((el) => {
      return el.id;
    }),
  );

  const buttonTitle = computed<string>(() => {
    if (isMobile.value) {
      return props.linkType === 'city' || props.linkType === 'region' ? t('selectCity') : t('selectCountry');
    }

    return props.linkType === 'city' || props.linkType === 'region' ? t('allCities') : t('allCountries');
  });

  const isCountries = computed(() => props.list.length);

  type SizeModal = 'fullscreen' | 'l' | 'm' | 's' | 'xs';

  const modalSize = computed<SizeModal>(() => {
    if (isDesktop.value || fromDesktopMedium.value) {
      return 'l';
    }

    if (isTablet.value) {
      return 'l';
    }

    return 'fullscreen';
  });

  const getParams = () => ({
    subject_geo_id: props.geoSubject?.id || 0,
    subject_geo_name: props.geoSubject?.name || '',
    subject_geo_type: props.geoType || '',
    cities_ids: citiesIds.value,
  });

  const vViewTracker = useViewTracker('geosubject-articles-info:cities-list:cities.view');

  const soguClickBtn = () => {
    trackEvent('geosubject-articles-info:cities-list:all_cities.view', getParams());
  };

  const modalItemClick = (el: GeoItem, url: string) => {
    trackEvent('geosubject-articles-info:cities-list:all_cities.click', {
      ...getParams(),
      city_id: el.id,
    });

    window.open(url, 'blank_');
  };

  const itemClick = (el: GeoItem, url: string) => {
    trackEvent('geosubject-articles-info:cities-list:cities.click', {
      ...getParams(),
      city_id: el.id,
    });

    window.open(url, 'blank_');
  };
</script>

<style lang="scss">
  .overlay {
    z-index: 100 !important;
  }

  #modal .modal-wrap {
    z-index: 11000 !important;
  }

  #modal .modal {
    z-index: 11001 !important;
  }
</style>

<style lang="scss" scoped>
  :deep(.icon) {
    display: none;
  }

  .skeleton-flex {
    display: flex;
    gap: 24px 16px;
  }
</style>
